// eslint-disable-next-line import/no-unresolved
import { Debug, MediaPlayer } from 'dashjs';
import { fromEvent, interval, merge, NEVER, of } from 'rxjs';
import { delay, distinctUntilChanged, filter, map, share, switchMap, take } from 'rxjs/operators';
import {
  AKAMAI_ERROR_HEADER,
  RENDERER_DEFAULT_ERROR,
  RENDERER_LIVE_STREAM_STALLED,
  RENDERER_MEDIA_ERROR,
  RENDERER_NETWORK_ERROR,
  RENDERER_PROTECTION_ERROR
} from '../../../error/definitions';
import { PLAYBACK_END } from '../../../store/types';
import { deepMerge } from '../../../utils';
import { bufferConsecutiveEvents, safeTake } from '../../../utils/rx-utils';
import Media from '../../media/Media';
import { DASH_TYPE, RENDERER_LIVE_STREAM_DETECTED } from '../types';
import RendererHTML, { HTML_EMPTY_SRC_MEDIA_ERROR } from './html';

export default class RendererDash extends RendererHTML {
  constructor(media, playerConfig, mediaUnlocker, isAd$) {
    super(media, playerConfig, mediaUnlocker, isAd$);
    this.serverForcedQuality = media.video.quality !== null && media.video.quality !== undefined;
    this.playerConfig = playerConfig;

    const { preload, debug, debugDetails } = this.playerConfig;
    this.preload = preload;

    this.setEngine(MediaPlayer().create());
    this.currentTrack = -1;

    this.currentSettings = {

      debug: {
        logLevel: debug && debugDetails?.renderer !== false ? Debug.LOG_LEVEL_DEBUG : Debug.LOG_LEVEL_NONE
      },
      streaming: {
        lastBitrateCachingInfo: {
          enabled: false
        },
        lastMediaSettingsCachingInfo: {
          enabled: false
        },
        abr: {
          autoSwitchBitrate: { video: !this.serverForcedQuality },
          initialBitrate: { audio: -1, video: media.video.quality }
        },
        text: {
          defaultEnabled: false
        },
        scheduling: {
          scheduleWhilePaused: preload
        },
        delay: {
          liveDelayFragmentCount: media.video.live_chunk_delay
        }
      }
    };

    this.currentSettings = deepMerge(
      this.currentSettings,
      playerConfig.isTv && RendererDash.getTVConfig(),
      media.video.renderer_config === 'STB3' && RendererDash.getSTB3Config()
    );

    this.engine.updateSettings(this.currentSettings);
    this.manifestLoaded$ = fromEvent(this.engine, MediaPlayer.events.MANIFEST_LOADED).pipe(share());

    this.engine.initialize(this.tagElement);

    this.engine.setAutoPlay(false);
    this.engine.setInitialMediaSettingsFor('audio', { role: 'main' });

    RendererDash.createPlaybackEndStream(this.engine).safeSubscribe(this, this.videoOrAudioEventsOut$);

    this.akamai = {
      ...media.video.akamai,
      urlManifest: media.video.urlManifest
    };

    this.drm = {
      ...media.video.drm,
      urlToken: media.video.token.drm,
      id: media.meta.id
    };
    if (this.drm?.active) {
      RendererDash.setupDRM(this.engine, this.drm);
    }

    RendererDash.detectQualityChange({ engine: this.engine }).safeSubscribe(this, this.bitrate$);
  }

  static getTVConfig() {
    return {
      streaming: {
        buffer: {
          fastSwitchEnabled: false,
          flushBufferAtTrackSwitch: true,
          bufferPruningInterval: 5,
          bufferToKeep: 10,
          avoidCurrentTimeRangePruning: true,
          bufferTimeAtTopQuality: 25,
          bufferTimeAtTopQualityLongForm: 25
        }
      }
    };
  }

  static getSTB3Config() {
    return {
      streaming: {
        buffer: {
          // to fix audio switch issues on NewBox
          reuseExistingSourceBuffers: false,
          avoidCurrentTimeRangePruning: true
        }
      }
    };
  }

  static createPlaybackEndStream(engine) {
    return fromEvent(engine, MediaPlayer.events.PLAYBACK_ENDED).pipe(
      map(() => PLAYBACK_END)
    );
  }

  startLoad() {
    return !this.preload && this.engine.play();
  }

  async init(container, target$, media, interruptions$) {
    /* super RendererHTML::init sets this.tagElement */
    super.init(container, target$, media, interruptions$);

    this.quanteec = await this.setupQuanteec(DASH_TYPE, media);
    this.tagElement.dirty = true;
    this.engine.attachView(this.tagElement);

    /* we need to create an empty DOM element for dash TTML handling to start working */
    this.engine.attachTTMLRenderingDiv(document.createElement('div'));
  }

  onMidrollStart() {
    this.pause();
    this.currentTrack = this.engine.getCurrentTextTrackIndex();
    this.startPosition = Math.trunc(this.tagElement.currentTime);
    this.source = this.engine.getSource();
    this.engine.reset();

    if (this.akamai?.active) {
      Media.requestCallToken(
        { video: { url: this.akamai.urlManifest } },
        this.akamai.token,
        ({ json: { url: tokenizedUrl } }) => tokenizedUrl
      ).pipe(take(1)).subscribe((token) => {
        this.source = token;
      });
    }

    setTimeout(() => this.suspendDuringMidroll$.next(true), 10);
  }

  initEngine() {
    super.configure(this.source);
    this.engine.initialize(this.tagElement, this.source, false, this.startPosition);
  }

  onMidrollEnd() {
    this.suspendDuringMidroll$.next(false);
    this.engine = MediaPlayer().create();
    this.engine$.next(this.engine);

    this.engine.updateSettings(this.currentSettings);

    if (this.drm?.active) {
      Media.requestcallDrm(
        { video: { drm: this.drm }, meta: { id: this.drm.id } },
        this.drm.urlToken,
        ({ json: { token } }) => token
      ).pipe(take(1)).subscribe((token) => {
        RendererDash.setupDRM(this.engine, {
          ...this.drm,
          token
        });
        this.initEngine();
      });
    } else {
      this.initEngine();
    }

    const streamInitializedOnce$ = fromEvent(
      this.engine,
      MediaPlayer.events.STREAM_INITIALIZED
    ).pipe(take(1));

    streamInitializedOnce$
      .subscribe(() => {
        this.setTextTrack(this.currentTrack);
        this.play();
      });
  }

  configure(src) {
    this.engine.attachView(this.tagElement);
    this.engine.attachSource(src, this.startPosition || undefined);
  }

  dispose() {
    this.engine.reset();
    this.engine.destroy();

    this.setEngine(null);

    super.dispose();
  }

  createErrorStream() {
    const dashErrors$ = merge(
      /* Handle raw DashJS errors */
      this.engine$.pipe(filter(Boolean), switchMap(() => fromEvent(this.engine, MediaPlayer.events.ERROR)
        .pipe(map((data) => RendererDash.formatErrors(data))))),
      /* detect live stalled via successive seeking events */
      this.isLive$.pipe(switchMap((isLive) => (isLive
        ? this.videoOrAudioEventsOut$.pipe(
          filter((evt) => evt !== 'progress'),
          bufferConsecutiveEvents({
            event: 'seeking',
            count: 3
          }),
          map(() => ({
            ...RENDERER_LIVE_STREAM_STALLED,
            code: RENDERER_LIVE_STREAM_STALLED.code + 200
          }))
        )
        : NEVER)))
    );

    const fatal$ = this.createRecoveryFailedStream(dashErrors$); /* implemented in GenericRenderer */

    const errors$ = merge(
      dashErrors$,
      fatal$,
      // Ignore the native "empty src attribute" when using dash.js !
      super.createErrorStream(this.isLive$).pipe(
        filter(
          ({
            payload: {
              originalError: { code }
            }
          }) => code !== HTML_EMPTY_SRC_MEDIA_ERROR.code
        )
      ) /* RendererHTML errors */
    );

    /* We do not want ad errors to trigger in the renderer.
   We delay a little bit in case the last ad is failing.
   In this case, the video tag would send the error after we think the midroll has ended
   and thus triggering an error on the renderer */
    return this.isMidroll$.pipe(
      delay(10),
      switchMap((isMidroll) => (isMidroll ? NEVER : errors$))
    );
  }

  createTimeshiftingDuration() {
    return merge(
      of(0),
      this.engine$.pipe(
        switchMap((engine) => fromEvent(engine, MediaPlayer.events.STREAM_INITIALIZED).pipe(
          switchMap((e) => {
            const { streamInfo: { manifestInfo } } = e;
            return interval(1000).pipe(map(() => {
              const now = Date.now();
              const startDate = manifestInfo.availableFrom.getTime();
              return (now - startDate) / 1000;
            }));
          }),
          distinctUntilChanged()
        ))
      )
    );
  }

  handleError({ error: { fatal, type, canRecover } }) {
    if (canRecover) {
      switch (type) {
        case RENDERER_NETWORK_ERROR.type: {
          this.engine.attachSource(this.engine.getSource());
          return this.engine.play();
        }
        case RENDERER_LIVE_STREAM_STALLED.type:
          return this.seek(this.engine.getDVRSeekOffset(this.engine.getDVRWindowSize()));
        default:
          break;
      }
    }
    return (fatal && this.dispose());
  }

  async attachSource(source) {
    this.configure(source);
  }

  handleStartPosition() {
    fromEvent(this.engine, MediaPlayer.events.CAN_PLAY).pipe(take(1)).subscribe(() => this.seek(this.startPosition));
  }

  handleTracksAdded() {
    fromEvent(this.engine, MediaPlayer.events.TEXT_TRACKS_ADDED)
      .pipe(map(({ tracks }) => tracks.map((track, index) => ({ kind: 'subtitles', ...track, index }))
        .filter((track) => track.mimeType === 'application/mp4')))
      .safeSubscribe(this, this.textTracks$);

    fromEvent(this.engine, MediaPlayer.events.CAN_PLAY)
      .pipe(take(1), map(this.getAudioTracks.bind(this)))
      .safeSubscribe(this, this.audioTracks$);
  }

  setVideoQuality(level) {
    if (this.serverForcedQuality) return;

    this.engine.updateSettings({
      streaming: {
        abr: {
          autoSwitchBitrate: { video: level < 0 }
        }
      }
    });
    if (level >= 0) {
      this.engine.setRepresentationForTypeByIndex('video', level);
    }
  }

  getAudioTracks() {
    const AUDIO_ORDER_DESC = [
      'description',
      'alternate',
      'main'
    ];

    const audioTracks = this.engine.getTracksFor('audio')
      .filter((track) => track.lang !== '')
      .sort((track1, track2) => {
        function getPrio(roles) {
          // no role is lower order than not found one
          if (!roles) {
            return -2;
          }

          return roles.reduce((prio, role) => AUDIO_ORDER_DESC.indexOf(role) + prio, 0);
        }

        return getPrio(track2.roles) - getPrio(track1.roles);
      })
      .map((track, index) => ({
        kind: 'audio', ...track, ...('index' in track ? {} : { index })
      }));
    return audioTracks;
  }

  createAvailableQualitiesStream() {
    return fromEvent(this.engine, MediaPlayer.events.STREAM_INITIALIZED)
      .pipe(
        map(() => this.engine.getRepresentationsByType('video')
          .map((q) => ({ ...q, bitrate: q?.bitrate || q.bandwidth }))
          .sort((a, b) => a.qualityIndex - b.qualityIndex))
      );
  }

  play() {
    this.engine.play();
  }

  setAudioTrack(index) {
    const selectedTrack = this.getAudioTracks().find((track) => track.index === index);
    this.engine.setCurrentTrack(selectedTrack);
  }

  setTextTrack(index) {
    this.engine.setTextTrack(index);
  }

  detectLiveStream(media) {
    return this.manifestLoaded$.pipe(
      filter(({ data: { type } }) => type === 'dynamic' && !media.isLive),
      safeTake(1),
      map(() => RENDERER_LIVE_STREAM_DETECTED)
    );
  }

  static detectQualityChange({ engine }) {
    return fromEvent(engine, MediaPlayer.events.QUALITY_CHANGE_RENDERED)
      .pipe(
        filter((data) => data?.mediaType === 'video'),
        map(({ newRepresentation: { index } }) => {
          const [{ bitrateList }] = engine.getTracksFor('video');
          const { bandwidth, height, width } = bitrateList[index];
          return {
            bandwidth,
            height,
            width,
            level: index
          };
        })
      );
  }

  static getHeader(networkDetails) {
    return networkDetails.getResponseHeader && networkDetails.getResponseHeader(AKAMAI_ERROR_HEADER);
  }

  static formatErrors(dashError) {
    const { error: { code, message, data } } = dashError;
    let error = {};
    switch (code) {
      /*
       * Single source of truth to escape the dash.js documentation maze:
       * https://cdn.dashjs.org/latest/jsdoc/core_errors_Errors.js.html
       * https://cdn.dashjs.org/latest/jsdoc/mss_errors_MssErrors.js.html
       * https://cdn.dashjs.org/latest/jsdoc/offline_errors_OfflineErrors.js.html
       * https://cdn.dashjs.org/latest/jsdoc/streaming_protection_errors_ProtectionErrors.js.html
       */
      case MediaPlayer.errors.DOWNLOAD_ERROR_ID_MANIFEST_CODE: {
        error = RENDERER_NETWORK_ERROR;

        if (data.response?.status === 403) {
          error = RendererHTML.resolveAkamaiErrorFor(RendererDash, data.response);
        }
        break;
      }
      case MediaPlayer.errors.DOWNLOAD_ERROR_ID_XLINK_CODE:
      case MediaPlayer.errors.DOWNLOAD_ERROR_ID_INITIALIZATION_CODE:
      case MediaPlayer.errors.DOWNLOAD_ERROR_ID_CONTENT_CODE: {
        const isChunk = data?.request.type === 'MediaSegment';
        const isLowestQuality = data?.request.quality <= 1;
        error = {
          ...RENDERER_NETWORK_ERROR,
          fatal: !isChunk || (isChunk && isLowestQuality),
          canRecover: !(!isChunk || (isChunk && isLowestQuality))
        };
        break;
      }
      case MediaPlayer.errors.MEDIA_KEY_MESSAGE_ERROR_CODE:
      case MediaPlayer.errors.MEDIA_KEY_MESSAGE_LICENSER_ERROR_CODE: // fire after 4 status 500 on /licence
      case MediaPlayer.errors.TIMED_TEXT_ERROR_ID_PARSE_CODE: {
        error = RENDERER_PROTECTION_ERROR;
        break;
      }
      case MediaPlayer.errors.MANIFEST_ERROR_ID_NOSTREAMS_CODE:
      case MediaPlayer.errors.MANIFEST_ERROR_ID_MULTIPLEXED_CODE:
      case MediaPlayer.errors.MEDIASOURCE_TYPE_UNSUPPORTED_CODE: {
        error = RENDERER_MEDIA_ERROR;
        break;
      }
      default: {
        error = RENDERER_DEFAULT_ERROR;
        break;
      }
    }

    return ({
      fatal: true,
      ...error,
      code: error.code + 200,
      payload: { message, data },
      description: code
    });
  }

  static overriddenKeySystemWidevine() {
    /* We are currying the widevine method to pass the vudrm token  */
    return () => ({
      getLicenseRequestFromMessage(message) {
        return message;
      }
    });
  }

  static setupDRM(engine, config) {
    engine.setProtectionData({
      'com.widevine.alpha': {
        serverURL: config.widevineLaUrl,
        httpRequestHeaders: {
          'nv-authorizations': config.token,
          'content-type': 'Application/octet-stream'
        },
        priority: config.widevineLaUrl.includes('wvls') ? 1 : 2
      },
      'com.microsoft.playready': {
        serverURL: config.playReadyLaUrl,
        httpRequestHeaders: {
          'nv-authorizations': config.token
        },
        priority: config.playReadyLaUrl.includes('prls') ? 1 : 2
      }
    });
  }
}
